import type {
  SpeciesData, PlotData, MetaData,
  LandmarkData, ComparisonData, ClusterData, PlotSimData,
  MapAdapter, Scene, SceneChangeDetail,
} from './types';
import { NavBar, type ChapterConfig } from './components/NavBar';
import { SceneIntro        } from './scenes/SceneIntro';
import { SceneClimate      } from './scenes/SceneClimate';
import { SceneTerrain      } from './scenes/SceneTerrain';
import { ScenePlaceholder  } from './scenes/ScenePlaceholder';
import { SceneLandmarks    } from './scenes/SceneLandmarks';
import { SceneSoilVsMopane } from './scenes/SceneSoilVsMopane';
import { Scene1            } from './scenes/Scene1';
import { Scene2            } from './scenes/Scene2';
import { SceneGroups           } from './scenes/SceneGroups';
import { SceneCarbonBaseline  } from './scenes/SceneCarbonBaseline';
import { SceneFire            } from './scenes/SceneFire';
import { SceneRivers          } from './scenes/SceneRivers';
import { SceneFlowDams        } from './scenes/SceneFlowDams';
import { SceneHydroManagement } from './scenes/SceneHydroManagement';

// All 3 landmark points visible from this view
const LANDMARKS_CENTER: [number, number] = [-15.849, 29.664];

// ── Scene map configs (one per scene, flat index) ─────────────────────────────
const SCENE_CONFIGS: {
  label: string;
  mapCenter: [number, number];
  mapZoom: number;
}[] = [
  // Chapter 0 — Introduction (placeholder)
  { label: 'Introduction',               mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
  // Chapter 1 — Baseline (3 scenes)
  { label: 'Climate Baseline',           mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
  { label: 'Topography Baseline',        mapCenter: [-15.90, 29.75], mapZoom: 9.5  },
  { label: 'Carbon Baseline',            mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
  // Chapter 2 — Fire Management
  { label: 'Savanna Fire Management',    mapCenter: [-15.75, 29.73], mapZoom: 10.2 },
  // Chapter 3 — Hydrology (3 scenes)
  { label: 'Rivers & Pooling',           mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
  { label: 'Flow & Dams',                mapCenter: [-15.83, 29.73], mapZoom: 10.5 },
  { label: 'Hydrology Management',       mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
  // Chapter 4 — Regeneration
  { label: 'Assisting Regeneration',     mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
  // Chapter 5 — Carbon Opportunities
  { label: 'Carbon Opportunities',       mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
  // Chapter 6 — Habitat & Biodiversity (6 scenes — Foundation Models first)
  { label: 'Foundation Models',          mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
  { label: 'Embedding Landmarks',        mapCenter: LANDMARKS_CENTER,  mapZoom: 10.5 },
  { label: 'Signal vs Habitat',          mapCenter: [-15.85, 29.85], mapZoom: 10.8 },
  { label: 'Species Distribution',       mapCenter: [-15.95, 29.68], mapZoom: 9.4  },
  { label: 'Species Signals',            mapCenter: [-15.75, 29.80], mapZoom: 8.5  },
  { label: 'Vegetation Assemblages',     mapCenter: [-15.85, 29.85], mapZoom: 8.8  },
];

// ── Chapter definitions ───────────────────────────────────────────────────────
const CHAPTERS: ChapterConfig[] = [
  { label: 'Introduction',           color: 'hsl(215,80%,60%)', sceneIndices: [0] },
  { label: 'Baseline',               color: 'hsl(160,70%,45%)', sceneIndices: [1, 2, 3] },
  { label: 'Fire Management',        color: 'hsl(25,90%,55%)',  sceneIndices: [4] },
  { label: 'Hydrology',              color: 'hsl(200,80%,50%)', sceneIndices: [5, 6, 7] },
  { label: 'Regeneration',           color: 'hsl(90,60%,45%)',  sceneIndices: [8] },
  { label: 'Carbon Opportunities',   color: 'hsl(140,60%,40%)', sceneIndices: [9] },
  { label: 'Habitat & Biodiversity', color: 'hsl(42,100%,62%)', sceneIndices: [10, 11, 12, 13, 14, 15] },
];

/**
 * Root story-map component — 7-chapter, 13-scene layout.
 *
 * Emits `sm:scenechange` CustomEvent<SceneChangeDetail> on every transition.
 */
export class StoryMap {
  private current = 0;
  private readonly scenes: Scene[];
  private readonly nav: NavBar;

  constructor(
    private readonly root: HTMLElement,
    species:    SpeciesData[],
    plots:      PlotData[],
    meta:       MetaData,
    landmarks:  LandmarkData[],
    _comparison: ComparisonData,
    clusters:   ClusterData[],
    plotSims:   PlotSimData[],
    climateCsv: string,
    private readonly map: MapAdapter,
    planetCogUri = '',
    soilCsv = '',
    gppCsv = '',
    biomassCsv = '',
  ) {
    this.scenes = [
      // Chapter 0 — Introduction (placeholder)
      new ScenePlaceholder(root, {
        chapter: 'Introduction',
        color: 'hsl(215,80%,60%)',
        description: 'Introduction content coming soon.',
      }),
      // Chapter 1 — Baseline
      new SceneClimate(root, climateCsv),
      new SceneTerrain(root, map, planetCogUri),
      new SceneCarbonBaseline(root, map, soilCsv, gppCsv, biomassCsv),
      // Chapter 2 — Fire Management
      new SceneFire(root, map),
      // Chapter 3 — Hydrology
      new SceneRivers(root, map),
      new SceneFlowDams(root, map),
      new SceneHydroManagement(root, map),
      // Chapter 4 — Regeneration
      new ScenePlaceholder(root, {
        chapter: 'Assisting Regeneration',
        color: 'hsl(90,60%,45%)',
        description:
          'Measuring vegetation recovery in degraded areas through time-series ' +
          'embedding analysis. Comparing habitat fingerprints before and after ' +
          'active planting or natural regeneration events quantifies intervention impact.',
      }),
      // Chapter 5 — Carbon Opportunities
      new ScenePlaceholder(root, {
        chapter: 'Carbon Opportunities',
        color: 'hsl(140,60%,40%)',
        description:
          'Mapping carbon stock baselines and identifying additionality zones ' +
          'across the concession using satellite-derived biomass and soil carbon proxies. ' +
          'Foundation model embeddings allow detection of above-ground carbon changes ' +
          'without repeated field campaigns.',
      }),
      // Chapter 6 — Habitat & Biodiversity (Foundation Models first)
      new SceneIntro(root),
      new SceneLandmarks(root, landmarks, map),
      new SceneSoilVsMopane(root, landmarks[0], species[0], map),
      new Scene1(root, plots, meta, map),
      new Scene2(root, species, clusters),
      new SceneGroups(root, clusters, plotSims, map),
    ];

    const sceneLabels = SCENE_CONFIGS.map(c => c.label);
    this.nav = new NavBar(root, CHAPTERS, sceneLabels, (i) => this.goTo(i));
    this.goTo(0);
  }

  goTo(index: number): void {
    if (index < 0 || index >= this.scenes.length) return;

    this.scenes[this.current].panel.classList.remove('active');
    this.scenes[this.current].onLeave();

    this.current = index;

    const cfg = SCENE_CONFIGS[index];
    this.scenes[index].panel.classList.add('active');
    this.scenes[index].onEnter();

    this.nav.update(index);
    this.map.flyTo(cfg.mapCenter, cfg.mapZoom);

    this.root.dispatchEvent(
      new CustomEvent<SceneChangeDetail>('sm:scenechange', {
        bubbles: true,
        detail: {
          index,
          label:     cfg.label,
          mapCenter: cfg.mapCenter,
          mapZoom:   cfg.mapZoom,
        },
      }),
    );
  }
}
